import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Tenant {
  id: number;
  name: string;
  nameDisplay: string;
  hasMFA: boolean;
  Domain: string;
  customerId?: string | null;
}

export interface Customer {
  id: number;
  name: string;
  nameDisplay: string;
  parentTenant:{
    parentTenantId: string;
  }
  customerId?: string | null;
}

export interface ProductListItem {
  id: number;
  name: string;
}

export interface UserProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  NumberPhone: string;
  position: string;
  meta_data: {
    mfa_active: boolean;
  };
  tenantId: number;
  Tenant: Tenant;
  TenantType: string;
  ProductList: ProductListItem[];
  ListPermiso: string[];
  hash: string;
  emailDomain: string;
  canAccessAlerts: boolean;
  gitbookToken?: string;
}

/**
 * Where the profile fetch stands. Without this, an in-flight profile is
 * indistinguishable from one that resolved with no products — the empty
 * `initialState` below is itself a valid resolved profile — so no consumer
 * could tell "still loading" from "loaded, nothing to show".
 */
export type UserProfileStatus = "idle" | "loading" | "ready" | "error";

export interface UserProfileState extends UserProfile {
  status: UserProfileStatus;
}

// Estado inicial...
const initialState: UserProfileState = {
  id: "",
  firstName: "",
  lastName: "",
  email: "",
  NumberPhone: "",
  position: "",
  meta_data: {
    mfa_active: false,
  },
  tenantId: 0,
  Tenant: {
    id: 0,
    name: "",
    nameDisplay: "",
    hasMFA: false,
    Domain: "",
  },
  TenantType: "",
  ProductList: [],
  ListPermiso: [],
  hash: "",
  emailDomain: "",
  canAccessAlerts: false, //set in false by default
  status: "idle",
};

export const userProfileSlice = createSlice({
  name: "userProfile",
  initialState,
  reducers: {
    /** The profile request is in flight. */
    setUserProfileLoading: (state) => {
      state.status = "loading";
    },
    setUserProfile: (state, action: PayloadAction<UserProfile>) => {
      const incoming = action.payload;
      //Extract domain from the incoming email
      const domain = incoming.email.includes("@")
        ? incoming.email.split("@")[1]
        : "";
      //Merge it all back into state, including emailDomain
      return {
        ...state,
        ...incoming,
        emailDomain: domain,
        // A profile that came back with no products is still `ready` — the rail
        // must settle into its empty state rather than animate forever.
        status: "ready" as const,
      };
      // return { ...state, ...action.payload };
    },
    /** The profile request failed. Consumers stop waiting and fall back. */
    setUserProfileError: (state) => {
      state.status = "error";
    },
    setEmailDomain: (state, action: PayloadAction<string>) => {
      state.emailDomain = action.payload;
    },
    clearUserProfile: () => {
      return initialState;
    },
  },
});

/**
 * Single source of truth for "the rail has nothing real to render yet".
 *
 * `idle` counts as unresolved on purpose: between first paint and the moment
 * `LoadUserProfileOnMount` fires its request, the store still holds the empty
 * `initialState`. Callers must not re-derive this from `ProductList.length`,
 * which would strand a legitimately product-less tenant in a permanent loader.
 */
export const selectIsUserProfileLoading = (state: {
  userProfile: UserProfileState;
}): boolean =>
  state.userProfile.status === "idle" || state.userProfile.status === "loading";

// Exporta acciones y reducer
export const {
  setUserProfile,
  setUserProfileLoading,
  setUserProfileError,
  clearUserProfile,
  setEmailDomain,
} = userProfileSlice.actions;
export default userProfileSlice.reducer;
